import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { formatMoney } from "@bw/shared/utils";

import type { MembershipPriceOverride, PricingRule } from "@/libs/ratesApi";
import type { PlanOption } from "./schema";

// The per-rule lazy member-price-override load state, keyed by rule_id. Mirrors
// the leagues season-detail RegState discipline: `entries` is undefined until a
// first successful fetch resolves; `error` is the per-rule banner, DISTINCT from
// an empty list so a failed fetch never masquerades as "No member overrides".
// The cache guard short-circuits on {loading || entries} but deliberately NOT on
// {error}, so a transient failure is retryable via collapse + re-expand.
export interface OverrideState {
  loading: boolean;
  entries?: MembershipPriceOverride[];
  error?: string;
}

// MemberPricingRow — the per-rule expanded "Member pricing" section. Rendered as
// an extra <TableRow> beneath its rule row (colSpan over the table). Shows: a
// per-rule loading affordance, a retryable error banner (NEVER a fabricated
// empty), an honest "No member overrides" empty (only after a load), and a small
// inner table of override rows — each resolving plan_id -> NAME via the
// planNameById lookup (DROPPED if unresolvable, mirroring the summary's
// resolve-or-drop) and member_price via formatMoney (number | string; no
// arithmetic). An inline add form picks a plan BY NAME (excluding plans already
// overridden for this rule) and a 2-decimal money input. Honesty: no raw
// override_id / plan_id / rule_id / facility_id ever reaches visible copy; the
// FE owns NO pricing math.

export interface MemberPricingRowProps {
  rule: PricingRule;
  state: OverrideState | undefined;
  planNameById: Map<string, string>;
  plans: PlanOption[];
  addOpen: boolean;
  addPlanId: string;
  addPrice: string;
  addOverrideValid: boolean;
  savingOverride: boolean;
  colSpan: number;
  onOpenAdd: () => void;
  onCloseAdd: () => void;
  onPlanChange: (planId: string) => void;
  onPriceChange: (price: string) => void;
  onSubmitAdd: () => void;
  onDelete: (override: MembershipPriceOverride) => void;
}

export function MemberPricingRow({
  rule,
  state,
  planNameById,
  plans,
  addOpen,
  addPlanId,
  addPrice,
  addOverrideValid,
  savingOverride,
  colSpan,
  onOpenAdd,
  onCloseAdd,
  onPlanChange,
  onPriceChange,
  onSubmitAdd,
  onDelete,
}: MemberPricingRowProps) {
  const entries = state?.entries;
  // Plans NOT yet overridden for this rule — the only ones offerable in the add
  // Select (DB UNIQUE(rule_id, plan_id) rejects a second override per plan).
  const overriddenPlanIds = new Set(
    (entries ?? []).map((entry) => entry.plan_id),
  );
  const availablePlans = plans.filter(
    (plan) => !overriddenPlanIds.has(plan.id),
  );

  return (
    <TableRow
      role="row"
      sx={{
        bgcolor: "action.hover",
        display: { xs: "block", md: "table-row" },
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <TableCell
        colSpan={colSpan}
        role="cell"
        sx={{
          display: { xs: "block", md: "table-cell" },
          px: 2,
          py: 1.5,
          whiteSpace: "normal",
        }}
      >
        <Stack spacing={1.5}>
          <Stack
            direction="row"
            spacing={1.5}
            sx={{ alignItems: "center", justifyContent: "space-between" }}
          >
            <Typography sx={{ fontWeight: 700 }} variant="body2">
              Member pricing
            </Typography>
            {entries && !addOpen ? (
              <Button
                disabled={availablePlans.length === 0}
                onClick={onOpenAdd}
                size="small"
                variant="outlined"
              >
                Add member price
              </Button>
            ) : null}
          </Stack>

          {state?.loading ? (
            <Typography color="text.secondary" variant="body2">
              Loading…
            </Typography>
          ) : state?.error ? (
            <Alert severity="error">{state.error}</Alert>
          ) : entries && entries.length === 0 && !addOpen ? (
            <Typography color="text.secondary" variant="body2">
              No member overrides.
            </Typography>
          ) : null}

          {entries ? (
            <>
              {entries.length > 0 ? (
                <Box sx={{ overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Plan</TableCell>
                        <TableCell>Member price</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {entries.map((override) => {
                        // Resolve plan_id -> NAME; DROP a row whose plan no longer
                        // resolves rather than leaking the raw UUID.
                        const planName = planNameById.get(override.plan_id);
                        if (!planName) return null;
                        return (
                          <TableRow key={override.override_id}>
                            <TableCell sx={{ fontWeight: 700 }}>
                              {planName}
                            </TableCell>
                            <TableCell>
                              {formatMoney(override.member_price)}
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                aria-label="Remove member price"
                                color="error"
                                onClick={() => onDelete(override)}
                                size="small"
                                title="Remove"
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              ) : null}

              {addOpen ? (
                <Paper sx={{ p: 2 }} variant="outlined">
                  <Stack spacing={2}>
                    <Box
                      sx={{
                        display: "grid",
                        gap: 2,
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(2, minmax(0, 1fr))",
                        },
                      }}
                    >
                      <FormControl size="small">
                        <InputLabel
                          id={`override-plan-label-${rule.rule_id}`}
                          shrink
                        >
                          Plan
                        </InputLabel>
                        {/* The selected-value display intentionally avoids echoing
                            a plan name before a successful create. */}
                        <Select
                          displayEmpty
                          id={`override-plan-${rule.rule_id}`}
                          label="Plan"
                          labelId={`override-plan-label-${rule.rule_id}`}
                          onChange={(event) => onPlanChange(event.target.value)}
                          renderValue={(value) => (
                            <Typography color="text.secondary" variant="body2">
                              {value ? "Selected" : "Pick a plan…"}
                            </Typography>
                          )}
                          value={addPlanId}
                        >
                          {availablePlans.map((plan) => (
                            <MenuItem key={plan.id} value={plan.id}>
                              {plan.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        disabled={savingOverride}
                        id={`override-price-${rule.rule_id}`}
                        label="Member price"
                        onChange={(event) => onPriceChange(event.target.value)}
                        placeholder="60.00"
                        size="small"
                        slotProps={{ htmlInput: { inputMode: "decimal" } }}
                        value={addPrice}
                      />
                    </Box>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ justifyContent: "flex-end" }}
                    >
                      <Button
                        disabled={savingOverride}
                        onClick={onCloseAdd}
                        size="small"
                        variant="outlined"
                      >
                        Cancel
                      </Button>
                      <Button
                        disabled={savingOverride || !addOverrideValid}
                        onClick={onSubmitAdd}
                        size="small"
                        variant="contained"
                      >
                        {savingOverride ? "Adding…" : "Add"}
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              ) : null}
            </>
          ) : null}
        </Stack>
      </TableCell>
    </TableRow>
  );
}
